import React, { useState, useEffect } from 'react'
import assets from '../assets/signinSignupBg'
import LoginPage from './LoginPage'
import SignupForm from './RegisterPage'
import StatusSelection from '../components/StatusSelection'

export const ScreenMode = {
  SIGN_IN: 'login',
  register: 'register',
  signup: 'signup',
} as const

export type ScreenModeType = (typeof ScreenMode)[keyof typeof ScreenMode]

interface LoginRegisterPagesProps {
  initialMode?: ScreenModeType
}

const LoginRegisterPages: React.FC<LoginRegisterPagesProps> = ({
  initialMode = ScreenMode.SIGN_IN,
}) => {
  const [width, setWidth] = useState(0)
  const [backgroundImage, setBackgroundImage] = useState(assets.images.signinBg)
  const [currMode, setCurrMode] = useState<ScreenModeType>(initialMode)
  const [selectedStatus, setSelectedStatus] = useState('')
  const [isMobile, setIsMobile] = useState(false)
  const [isTablet, setIsTablet] = useState(false)

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const screenParam = urlParams.get('screen') as ScreenModeType | null

    if (screenParam && Object.values(ScreenMode).includes(screenParam)) {
      setCurrMode(screenParam)
      setBackgroundImage(
        screenParam === ScreenMode.SIGN_IN
          ? assets.images.signinBg
          : assets.images.signupBg,
      )
    }
  }, [])

  useEffect(() => {
    const handleResize = () => {
      const windowWidth = window.innerWidth
      setIsMobile(windowWidth < 1100)
      setIsTablet(windowWidth >= 600 && windowWidth < 1100)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleModeChange = (mode: ScreenModeType) => {
    setWidth(100)

    setTimeout(() => {
      setCurrMode(mode)
      setBackgroundImage(
        mode === ScreenMode.SIGN_IN
          ? assets.images.signinBg
          : assets.images.signupBg,
      )

      const url = new URL(window.location.href)
      if (mode === ScreenMode.SIGN_IN) {
        url.searchParams.delete('screen')
      } else {
        url.searchParams.set('screen', mode)
      }
      window.history.pushState({}, '', url)
    }, 600)

    setTimeout(() => setWidth(0), 500)
  }

  return (
    <div
      className={`flex ${
        isMobile ? 'flex-col' : 'flex-row'
      } h-screen overflow-hidden`}
    >
      {currMode === ScreenMode.SIGN_IN && (
        <div
          className={`flex-[0.5] flex justify-center items-center h-screen bg-white
          ${isMobile ? 'min-h-screen py-10' : 'absolute right-0'}
          ${isTablet ? 'w-full' : 'w-1/2 px-0'}
          ${isMobile ? 'w-full px-2' : ''}
        `}
        >
          <LoginPage onSwitchMode={handleModeChange} />
        </div>
      )}
      {currMode === ScreenMode.register && (
        <div
          className={`flex-[0.5] flex justify-center items-center h-screen bg-white absolute left-0
          ${isTablet ? 'w-full' : 'w-1/2 px-0'}
          ${isMobile ? 'w-full' : ''}
          p-1 sm:p-4
        `}
        >
          <StatusSelection
            status={selectedStatus}
            handleStatusChange={setSelectedStatus}
            onSwitchMode={handleModeChange}
            setStatusSelected={() => handleModeChange(ScreenMode.signup)}
          />
        </div>
      )}
      {currMode === ScreenMode.signup && (
        <div
          className={`flex-[0.5] flex justify-center items-center bg-white
          ${isTablet ? 'w-full' : 'w-1/2 px-0'}
          ${isMobile ? 'w-full' : ''}
          p-1 sm:p-4
        `}
        >
          <SignupForm
            onSwitchMode={handleModeChange}
            initialStatus={selectedStatus}
          />
        </div>
      )}
      {!isMobile && (
        <>
          <div
            className='h-screen absolute top-0 bg-[#ff005a] transition-all duration-1000'
            style={{ width: `${width}%` }}
          />
          <div
            className='flex-1 bg-center bg-cover bg-no-repeat'
            style={{ backgroundImage: `url(${backgroundImage})` }}
          >
            <div
              className='h-screen absolute top-0 bg-[#222121] transition-all duration-1000'
              style={{ width: `${width}%` }}
            />
          </div>
        </>
      )}
    </div>
  )
}

export default LoginRegisterPages
